import type { ReactNode } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Button, Markdown } from '@highlight/ui'
import { useAuth } from '@renderer/chat/providers'
import { braintrustSpanPermalink } from '@renderer/overlay/Sidepanel/components/braintrust-links'
import {
  summarizePlannerDebugRun,
  type PlannerDebugAction,
  type PlannerDebugReminder,
  type PlannerDebugRunDetail,
  type PlannerDebugSkillRun,
} from './planner-debug-api'
import {
  formatPlannerDebugDate,
  PlannerDebugBadge,
  PlannerDebugError,
  PlannerDebugHeading,
  PlannerDebugJson,
} from './planner-debug-shared'

/**
 * Everything the detail route returns for one run, stacked: actions and
 * reminders, spawned skill chats, the planner scratchpad, and an on-demand
 * summary. One fetch, no tabs — a debugging admin wants it all on screen, and
 * only the summary costs anything (a model call), so only it sits behind a
 * button.
 */
export function PlannerDebugRunDetailView({ noteId, detail }: { noteId: string; detail: PlannerDebugRunDetail }) {
  return (
    <div className="flex flex-col gap-4">
      <PlannerDebugBlock title={`Actions & reminders (${detail.actions.length + detail.reminders.length})`}>
        <PlannerDebugActionsBlock detail={detail} />
      </PlannerDebugBlock>
      <PlannerDebugBlock title={`Skill chats (${detail.skillRuns.length})`}>
        <PlannerDebugSkillsBlock detail={detail} />
      </PlannerDebugBlock>
      <PlannerDebugBlock title="Scratchpad">
        <PlannerDebugScratchpadBlock detail={detail} />
      </PlannerDebugBlock>
      <PlannerDebugBlock title="Summary">
        <PlannerDebugSummaryBlock noteId={noteId} jobId={detail.run.id} />
      </PlannerDebugBlock>
    </div>
  )
}

function PlannerDebugBlock({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <PlannerDebugHeading>{title}</PlannerDebugHeading>
      {children}
    </div>
  )
}

function PlannerDebugActionsBlock({ detail }: { detail: PlannerDebugRunDetail }) {
  if (detail.actions.length === 0 && detail.reminders.length === 0) {
    return <p className="text-foreground-secondary type-ui-xs">This run staged no actions or reminders.</p>
  }

  return (
    <div className="flex flex-col gap-2">
      {detail.actions.map((action) => (
        <PlannerDebugActionRow key={action.id} action={action} />
      ))}
      {detail.reminders.map((reminder) => (
        <PlannerDebugReminderRow key={reminder.id} reminder={reminder} />
      ))}
    </div>
  )
}

function PlannerDebugActionRow({ action }: { action: PlannerDebugAction }) {
  const status = action.undoneAt
    ? { label: 'undone', tone: 'warn' as const }
    : action.resolvedAt
      ? { label: 'resolved', tone: 'good' as const }
      : action.archivedAt
        ? { label: 'archived', tone: 'neutral' as const }
        : action.outputData != null
          ? { label: 'completed', tone: 'good' as const }
          : { label: 'drafted', tone: 'neutral' as const }

  return (
    <details className="corner-smooth rounded-lg border p-3">
      <summary className="flex cursor-pointer flex-wrap items-center gap-2">
        <span className="text-foreground type-ui-xs font-medium">{action.title || action.toolName || action.id}</span>
        {action.toolSet && <PlannerDebugBadge>{action.toolSet}</PlannerDebugBadge>}
        <PlannerDebugBadge tone={status.tone}>{status.label}</PlannerDebugBadge>
        <span className="text-foreground-secondary type-ui-xs ml-auto">{formatPlannerDebugDate(action.createdAt)}</span>
      </summary>
      <div className="mt-2 flex flex-col gap-2">
        {action.description && <p className="text-foreground-secondary type-ui-xs">{action.description}</p>}
        {action.explanation && <p className="text-foreground-secondary type-ui-xs italic">{action.explanation}</p>}
        <p className="text-foreground-secondary type-code-xs">
          {action.toolSet} · {action.toolName} · {action.id}
          {action.lastInvokedAt ? ` · invoked ${formatPlannerDebugDate(action.lastInvokedAt)}` : ''}
        </p>
        {action.braintrustSpanId && (
          <a
            href={braintrustSpanPermalink(action.braintrustSpanId)}
            target="_blank"
            rel="noreferrer"
            className="text-foreground-secondary hover:text-foreground type-ui-xs w-fit underline"
          >
            Open authoring trace
          </a>
        )}
        <PlannerDebugJson label="Input data" value={action.inputData} />
        <PlannerDebugJson label="Output data" value={action.outputData} />
      </div>
    </details>
  )
}

function PlannerDebugReminderRow({ reminder }: { reminder: PlannerDebugReminder }) {
  return (
    <div className="corner-smooth flex flex-col gap-1 rounded-lg border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-foreground type-ui-xs font-medium">{reminder.text}</span>
        <PlannerDebugBadge>{reminder.type}</PlannerDebugBadge>
        {reminder.priority && <PlannerDebugBadge tone="warn">priority</PlannerDebugBadge>}
        {reminder.resolvedAt && <PlannerDebugBadge tone="good">resolved</PlannerDebugBadge>}
        <span className="text-foreground-secondary type-ui-xs ml-auto">
          {formatPlannerDebugDate(reminder.createdAt)}
        </span>
      </div>
      {reminder.description && <p className="text-foreground-secondary type-ui-xs">{reminder.description}</p>}
      <p className="text-foreground-secondary type-ui-xs">
        {reminder.dueAt ? `Due ${formatPlannerDebugDate(reminder.dueAt)}` : 'No due date'}
        {reminder.source?.title ? ` · from “${reminder.source.title}”` : ''}
      </p>
    </div>
  )
}

function PlannerDebugSkillsBlock({ detail }: { detail: PlannerDebugRunDetail }) {
  if (detail.skillRuns.length === 0) {
    return <p className="text-foreground-secondary type-ui-xs">This run spawned no skill chats.</p>
  }

  return (
    <div className="flex flex-col gap-2">
      {detail.skillRuns.map((skillRun) => (
        <PlannerDebugSkillCard key={skillRun.conversationId} skillRun={skillRun} detail={detail} />
      ))}
    </div>
  )
}

function PlannerDebugSkillCard({
  skillRun,
  detail,
}: {
  skillRun: PlannerDebugSkillRun
  detail: PlannerDebugRunDetail
}) {
  const tone = skillRun.state === 'responded' ? 'good' : skillRun.state === 'running' ? 'neutral' : 'warn'
  const actionsById = new Map(detail.actions.map((action) => [action.id, action]))

  return (
    <div className="corner-smooth flex flex-col gap-2 rounded-lg border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-foreground type-ui-xs font-medium">{skillRun.skill ?? 'Unknown skill'}</span>
        {skillRun.toolset && <PlannerDebugBadge>{skillRun.toolset}</PlannerDebugBadge>}
        {skillRun.sourceIntegration && <PlannerDebugBadge>{skillRun.sourceIntegration}</PlannerDebugBadge>}
        <PlannerDebugBadge tone={tone}>{skillRun.state}</PlannerDebugBadge>
      </div>
      <p className="text-foreground-secondary type-code-xs break-all">{skillRun.conversationId}</p>
      {skillRun.prompt ? (
        <details>
          <summary className="text-foreground-secondary hover:text-foreground type-ui-xs cursor-pointer">
            Kickoff prompt ({Math.round(skillRun.prompt.length / 1000)}k chars
            {skillRun.promptTruncated ? ', truncated' : ''})
          </summary>
          <pre className="text-foreground-secondary type-code-xs mt-2 max-h-80 overflow-auto whitespace-pre-wrap">
            {skillRun.prompt}
          </pre>
        </details>
      ) : (
        <p className="text-foreground-secondary type-ui-xs">No stored kickoff prompt.</p>
      )}
      {skillRun.firstResponse ? (
        <div className="flex flex-col gap-1">
          <p className="text-foreground-secondary type-ui-xs">
            First assistant response{skillRun.firstResponseTruncated ? ' (truncated)' : ''}
          </p>
          <pre className="text-foreground-secondary type-code-xs max-h-80 overflow-auto whitespace-pre-wrap">
            {skillRun.firstResponse}
          </pre>
        </div>
      ) : (
        <p className="text-foreground-secondary type-ui-xs">
          {skillRun.state === 'running' ? 'Still running — no response yet.' : 'No assistant response was recorded.'}
        </p>
      )}
      {skillRun.stagedActionIds.length > 0 && (
        <div className="flex flex-col gap-1">
          <span className="text-foreground-secondary type-ui-xs">Staged actions</span>
          {skillRun.stagedActionIds.map((actionId) => {
            const action = actionsById.get(actionId)
            return (
              <span key={actionId} className="text-foreground-secondary type-ui-xs">
                {action ? `${action.toolSet}/${action.toolName} — ${action.title ?? actionId}` : actionId}
              </span>
            )
          })}
        </div>
      )}
    </div>
  )
}

function PlannerDebugScratchpadBlock({ detail }: { detail: PlannerDebugRunDetail }) {
  const { scratchpad } = detail
  if (scratchpad.status === 'available') {
    return (
      <pre className="text-foreground-secondary type-code-xs max-h-[500px] overflow-auto whitespace-pre-wrap">
        {scratchpad.value || '(empty scratchpad)'}
      </pre>
    )
  }
  if (scratchpad.status === 'running') {
    return (
      <p className="text-foreground-secondary type-ui-xs">
        The planner workflow is still running — refreshing until it finishes.
      </p>
    )
  }
  if (scratchpad.status === 'errored') {
    return (
      <p className="text-danger-subtle type-ui-xs">
        The workflow errored{scratchpad.error ? `: ${scratchpad.error}` : '.'}
      </p>
    )
  }
  return <p className="text-foreground-secondary type-ui-xs">The workflow output has expired or is missing.</p>
}

function PlannerDebugSummaryBlock({ noteId, jobId }: { noteId: string; jobId: string }) {
  const { getAuthToken } = useAuth()
  const summary = useMutation({ mutationFn: () => summarizePlannerDebugRun(noteId, jobId, getAuthToken) })

  return (
    <div className="flex flex-col items-start gap-2">
      {!summary.data && (
        <Button variant="outline" loading={summary.isPending} onClick={() => summary.mutate()}>
          Generate summary
        </Button>
      )}
      {summary.isError && <PlannerDebugError error={summary.error} />}
      {summary.data && (
        <>
          {summary.data.scratchpadStatus !== 'available' && (
            <p className="text-foreground-secondary type-ui-xs">
              The scratchpad was {summary.data.scratchpadStatus}; this summary uses the available outputs.
            </p>
          )}
          <Markdown size="sm" className="text-foreground-secondary">
            {summary.data.summary}
          </Markdown>
        </>
      )}
    </div>
  )
}
